import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { deleteLecture, getAdminLectures, getLectureCategories, getLecturers } from '../../../services/admin/classApi';
import { AdminLecture, AdminLectureListParams, LectureCategory, Lecturer } from '../../../types/admin';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import CommunityTablePagination from '../community/CommunityTablePagination';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  EmptyText,
  HeaderCell,
  HeaderText,
  RetryButton,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
} from '../common/AdminTablePrimitives';
import {
  PrimaryButton,
  SecondaryButton,
} from '../common/AdminModalPrimitives';
import AdminDateFilterModal from '../common/AdminDateFilterModal';
import {
  DateButton,
  DateButtonIcon,
  DateButtonText,
} from '../common/AdminFilterPrimitives';
import AdminFilterDropdown from '../common/AdminFilterDropdown';
import AdminSearchInput from '../common/AdminSearchInput';

const LectureManagementComponent: React.FC = () => {
  const navigate = useNavigate();
  const [lectures, setLectures] = useState<AdminLecture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<LectureCategory[]>([]);
  const [lecturers, setLecturers] = useState<Lecturer[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLecturer, setSelectedLecturer] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDateModal, setShowDateModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [modalEndDate, setModalEndDate] = useState('');
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 0, totalElements: 0 });

  // 초기 로딩 여부 추적 (첫 번째 fetch 완료 전까지만 true)
  const isFirstLoad = useRef(true);

  const fetchCategories = useCallback(async () => {
    try {
      setCategories(await getLectureCategories());
    } catch {}
  }, []);

  const fetchLecturers = useCallback(async () => {
    try {
      setLecturers(await getLecturers());
    } catch {}
  }, []);

  const fetchLectures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: AdminLectureListParams = {
        page: pagination.page,
        size: pagination.size,
        categoryId: selectedCategory || undefined,
        dateFrom: selectedDate || undefined,
        dateTo: endDate || undefined,
        lecturerId: selectedLecturer || undefined,
        status: selectedStatus === '' ? undefined : selectedStatus === 'true',
        search: searchQuery || undefined,
      };
      const response = await getAdminLectures(params);
      setLectures(response.content);
      setPagination((prev) => ({ ...prev, totalPages: response.totalPages, totalElements: response.totalElements }));
    } catch {
      setError('강의 목록을 불러오는데 실패했습니다.');
    } finally {
      isFirstLoad.current = false;
      setLoading(false);
    }
  }, [endDate, pagination.page, pagination.size, searchQuery, selectedCategory, selectedDate, selectedLecturer, selectedStatus]);

  useEffect(() => {
    fetchCategories();
    fetchLecturers();
  }, [fetchCategories, fetchLecturers]);

  useEffect(() => {
    fetchLectures();
  }, [fetchLectures]);

  const handleDelete = async (lectureId: number) => {
    if (!window.confirm('강의를 삭제하시겠습니까?')) {
      return;
    }
    try {
      await deleteLecture(lectureId);
      fetchLectures();
    } catch {
      alert('강의 삭제에 실패했습니다.');
    }
  };

  const isInitialLoading = loading && isFirstLoad.current;
  if (isInitialLoading) {
    return (
      <AdminPage>
        <AdminPageHeader><AdminPageTitle>강의 관리</AdminPageTitle></AdminPageHeader>
        <AdminSection><StateMessage>로딩 중...</StateMessage></AdminSection>
      </AdminPage>
    );
  }

  if (error && lectures.length === 0) {
    return (
      <AdminPage>
        <AdminPageHeader><AdminPageTitle>강의 관리</AdminPageTitle></AdminPageHeader>
        <AdminSection><StateMessage>{error}</StateMessage><RetryButton onClick={fetchLectures}>다시 시도</RetryButton></AdminSection>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader><AdminPageTitle>강의 관리</AdminPageTitle></AdminPageHeader>
      <AdminSection>
        <FilterRow>
          <FilterGroup>
            <AdminFilterDropdown
              value={selectedCategory || ''}
              onChange={(value) => { setSelectedCategory(value ? Number(value) : null); setPagination((prev) => ({ ...prev, page: 0 })); }}
              maxWidth="180px"
              options={[
                { value: '', label: '카테고리' },
                ...categories.map((category) => ({ value: category.id, label: category.name })),
              ]}
            />
            <DateButton type="button" onClick={() => setShowDateModal(true)}>
              <DateButtonText>
                {selectedDate && endDate ? `${selectedDate} ~ ${endDate}` : selectedDate ? `${selectedDate} ~` : '등록일'}
              </DateButtonText>
              <DateButtonIcon width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </DateButtonIcon>
            </DateButton>
            <AdminFilterDropdown
              value={selectedLecturer || ''}
              onChange={(value) => { setSelectedLecturer(value ? Number(value) : null); setPagination((prev) => ({ ...prev, page: 0 })); }}
              maxWidth="180px"
              options={[
                { value: '', label: '강사' },
                ...lecturers.map((lecturer) => ({ value: lecturer.id, label: lecturer.name })),
              ]}
            />
            <AdminFilterDropdown
              value={selectedStatus}
              onChange={(value) => { setSelectedStatus(value); setPagination((prev) => ({ ...prev, page: 0 })); }}
              maxWidth="140px"
              options={[
                { value: '', label: '상태' },
                { value: 'true', label: '등록 완료' },
                { value: 'false', label: '임시 저장' },
              ]}
            />
          </FilterGroup>
          <AdminSearchInput
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            placeholder="강의 제목을 입력해주세요."
          />
        </FilterRow>
        <TableContainer>
          <TableInner>
            <TableHeader>
              <TableHeaderRow $columns="60px minmax(150px, 3fr) minmax(90px, 1.8fr) minmax(80px, 1.6fr) 110px 110px 110px">
                <HeaderCell><HeaderText>번호</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>제목</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>카테고리</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>강사</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>상태</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>등록일</HeaderText></HeaderCell>
                <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {lectures.length > 0 ? lectures.map((lecture, index) => (
                <TableRow key={lecture.id} $columns="60px minmax(150px, 3fr) minmax(90px, 1.8fr) minmax(80px, 1.6fr) 110px 110px 110px">
                  <BodyCell><CellText>{pagination.totalElements - pagination.page * pagination.size - index}</CellText></BodyCell>
                  <BodyCell><EllipsisText title={lecture.title}>{lecture.title}</EllipsisText></BodyCell>
                  <BodyCell><CellText>{lecture.category_name}</CellText></BodyCell>
                  <BodyCell><CellText>{lecture.lecturer_name}</CellText></BodyCell>
                  <BodyCell><StatusText $active={lecture.status}>{lecture.status ? '등록 완료' : '임시 저장'}</StatusText></BodyCell>
                  <BodyCell><CellText>{new Date(lecture.created_at).toLocaleDateString('ko-KR')}</CellText></BodyCell>
                  <BodyCell>
                    <ActionButtons>
                      <EditButton $tone="primary" onClick={() => navigate(`/admin/class/lecture/register?lectureId=${lecture.id}`)}>수정</EditButton>
                      <ActionButton $tone="danger" onClick={() => handleDelete(lecture.id)}>삭제</ActionButton>
                    </ActionButtons>
                  </BodyCell>
                </TableRow>
              )) : <EmptyRow><EmptyText>등록된 강의가 없습니다.</EmptyText></EmptyRow>}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={pagination.page + 1}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page: page - 1 }))}
          />
        </TableContainer>
      </AdminSection>
      {showDateModal && (
        <AdminDateFilterModal
          title="등록일 선택"
          startDate={startDate}
          endDate={modalEndDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setModalEndDate}
          onCancel={() => setShowDateModal(false)}
          onConfirm={() => {
            setSelectedDate(startDate);
            setEndDate(modalEndDate);
            setPagination((prev) => ({ ...prev, page: 0 }));
            setShowDateModal(false);
          }}
        />
      )}
    </AdminPage>
  );
};

const FilterRow = styled.div`display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;`;
const FilterGroup = styled.div`display:flex;align-items:flex-start;gap:8px;flex-wrap:wrap;`;
const StateMessage = styled.div`padding:40px;text-align:center;color:#666;font-size:16px;`;
const EmptyRow = styled.div`padding:48px 20px;display:flex;justify-content:center;`;
const EllipsisText = styled.span`display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#171719;font-size:16px;`;
const StatusText = styled.span<{ $active: boolean }>`color:${({ $active }) => ($active ? '#296768' : 'rgba(55,56,60,.61)')};font-size:16px;white-space:nowrap;`;
const EditButton = styled(ActionButton)`
  color: #171719;

  &:hover {
    background-color: rgba(23, 23, 25, 0.05);
  }
`;
export default LectureManagementComponent;
